import mapboxgl from 'mapbox-gl';
import { useEffect, useMemo, useRef } from 'react';

import { fitBounds } from '../../modules/referenceView/Tkv2MlsMap/fitBounds.ts';
import MapBox from '../map/MapBox.ts';
import { MAP_SOURCES } from '../map/constants.ts';
import { useMap } from '../map/useMap.ts';
import { VacantLandSalesMapProps } from './types.ts';

const DEFAULT_ZOOM = 14;

export const useVacantLandSalesMap = ({
  vacantLandSales,
  focusedPropertyId,
  setFocusedPropertyId,
  subjectCoordinates,
}: VacantLandSalesMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapBox | null>(null);
  const propertyLoaded = useRef<boolean>(false);
  const focusedPropertyIdRef = useRef<string | undefined>(focusedPropertyId);
  const subjectLatLng = { lat: subjectCoordinates.latitude, lng: subjectCoordinates.longitude };

  const landSalesWithoutSubject = vacantLandSales.filter(
    (prop) =>
      prop.latitude !== subjectCoordinates.latitude ||
      prop.longitude !== subjectCoordinates.longitude,
  );

  const mapboxSourceData = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: landSalesWithoutSubject.map(({ latitude, longitude, id }) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [longitude as number, latitude as number],
        },
        properties: {
          id,
          icon: focusedPropertyId === id ? 'Active' : 'Off-Market',
        },
      })),
    }),
    [landSalesWithoutSubject, focusedPropertyId],
  );

  useMap({
    mapContainerRef,
    mapRef,
    center: subjectLatLng,
    zoom: DEFAULT_ZOOM,
    subject: subjectLatLng,
    pins: [],
    callbacks: {
      onPinClick: (feature) =>
        setFocusedPropertyId((feature as unknown as { properties: { id: string } }).properties.id),
      onPinSourceLoad: (sourceId, target) => {
        if (propertyLoaded.current && focusedPropertyIdRef.current === focusedPropertyId) return;
        focusedPropertyIdRef.current = focusedPropertyId;

        const source = target.getSource(sourceId) as mapboxgl.GeoJSONSource;
        source.setData(mapboxSourceData);

        fitBounds(target, [...landSalesWithoutSubject, subjectCoordinates]);
        propertyLoaded.current = true;
      },
    },
  });

  useEffect(() => {
    (mapRef.current?.getMap()?.getSource(MAP_SOURCES.PINS) as mapboxgl.GeoJSONSource)?.setData(
      mapboxSourceData,
    );
  }, [mapboxSourceData]);

  return { mapContainerRef };
};
